/* =====================================================================
 * @auw/i18n — public API re-export surface.
 *
 * All runtime work (i18next init, postProcessor registration, initial
 * locale detection) happens in `./bootstrap` so child modules can
 * `import i18n from './bootstrap'` without an import cycle through
 * this barrel. Hosts should import from the package root:
 *
 *   import i18n, {
 *     useAppLocale,
 *     formatDate,
 *     workspaceModeToAgentProfileId,
 *   } from '@auw/i18n';
 * ===================================================================== */

import { activeTenantKey } from '@auw/tenant';

import i18n from './bootstrap';

export default i18n;

export { LOCALE_STORAGE_KEY, useAppLocale } from './useAppLocale';
export { SUPPORTED_LOCALES, resolveAppLocale, toIntlLocale } from './resolveAppLocale';
export type { AppLocale } from './resolveAppLocale';
export { NAMESPACES, RESOURCES } from './resources';
export type { Namespace } from './resources';
export { formatDate, formatDateTime, formatNumber, formatRelativeTime } from './formatters';

/* Utility re-exports (agent profile + profile locale / interests).
 * They live inside `@auw/i18n` because each one consumes translation
 * keys (`i18n.t(...)`), so bundling them next to the resources keeps
 * the dependency direction one-way: callers depend on `@auw/i18n`,
 * never the reverse. */
export {
  AGENT_PROFILE_IDS,
  AGENT_PROFILE_META,
  getAgentProfileMeta,
  useIsAgentProfileVisible,
  agentProfileVisible,
  isAgentProfileId,
  workspaceModeToAgentProfileId,
  dmAgentTypeToAgentProfileId,
} from './agentProfile';
export type { AgentProfileId, AgentProfileMeta } from './agentProfile';

export {
  PROFILE_LANGUAGE_OPTIONS,
  PROFILE_COUNTRY_OPTIONS,
  getProfileLanguageOptions,
  getProfileCountryOptions,
  normalizeProfileLanguageCode,
  normalizeProfileCountryCode,
  getDefaultLanguageForCountry,
  getCountryCodeFromLanguage,
} from './profileLocale';
export type { ProfileLocaleOption } from './profileLocale';

export {
  JOB_ROLE_GROUPS,
  ALL_JOB_ROLES,
  INTEREST_OPTIONS,
  MAX_INTERESTS,
  MIN_INTERESTS,
  MAX_INTEREST_OPTION_COUNT,
  MAX_CUSTOM_INTEREST_LENGTH,
  getJobRoleLabel,
  getJobRoleCategoryLabel,
  sanitizeInterestKeyword,
  normalizeInterestKeyword,
  isPresetInterestValue,
  mergeCustomInterestLabels,
  getInterestDisplayLabel,
} from './profileJobInterests';
export type { JobRoleGroup, InterestOption } from './profileJobInterests';

/* Tenant-scoped storage key for the selected UI locale.
 * Exported so host-side settings pages can warm-read / reset it. */
export const APP_LOCALE_STORAGE_KEY: string = activeTenantKey('app_locale');
